import React, { useMemo } from 'react';
import pick from 'lodash/pick';
import { graphql, useFragment } from 'react-relay';
import type { groupRow_nativeIssueTable_FirstGroupRow_firstGroupRow$key } from '@atlassian/jira-relay/src/__generated__/groupRow_nativeIssueTable_FirstGroupRow_firstGroupRow.graphql';
import type {
	groupRow_nativeIssueTable_groupRow$data,
	groupRow_nativeIssueTable_groupRow$key,
} from '@atlassian/jira-relay/src/__generated__/groupRow_nativeIssueTable_groupRow.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import { useIsItemExpanded } from '../../../controllers/hierarchy/index.tsx';
import { getChildQueryVariablesForJql } from '../../hierarchy/utils.tsx';
import { useRowContext } from '../../rows/row-context/index.tsx';
import { FirstGroupIssues } from './first-group-issues/index.tsx';
import { useInitialiseFirstGroupIssues } from './first-group-issues/utils.tsx';
import { GroupHeader } from './group-header/index.tsx';

export type BaseProps = {
	groupConnectionId: string;
	isVirtual: boolean;
};

type FirstGroupRowProps = BaseProps & {
	firstGroupRow: groupRow_nativeIssueTable_FirstGroupRow_firstGroupRow$key;
	loading: boolean;
};

const getGroupFieldValue = (
	fieldValue: groupRow_nativeIssueTable_groupRow$data['fieldValue'],
): string | null => {
	switch (fieldValue?.__typename) {
		case 'JiraJqlPriorityFieldValue':
			return fieldValue?.priority?.priorityId ?? null;
		case 'JiraJqlUserFieldValue':
			return fieldValue?.user?.accountId ?? null;
		case 'JiraJqlStatusFieldValue':
			return fieldValue?.status?.id ?? null;
		case 'JiraJqlNumberFieldValue':
			return fieldValue?.number?.toString() ?? null;
		case 'JiraJqlOptionFieldValue':
			return fieldValue?.option?.optionId ?? null;
		case 'JiraJqlSprintFieldValue':
			return fieldValue?.sprint?.sprintId ?? null;
		case 'JiraJqlGoalsFieldValue':
			return fieldValue?.goal?.id ?? null;
		default:
			return null;
	}
};

export const FirstGroupRow = ({
	firstGroupRow,
	groupConnectionId,
	isVirtual,
	loading,
}: FirstGroupRowProps) => {
	const cloudId = useCloudId();
	const firstGroupRowData = useFragment<groupRow_nativeIssueTable_FirstGroupRow_firstGroupRow$key>(
		graphql`
			fragment groupRow_nativeIssueTable_FirstGroupRow_firstGroupRow on JiraSpreadsheetGroup
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				isReparentingEnabled: { type: "Boolean", defaultValue: false }
				isDensityFull: { type: "Boolean", defaultValue: false }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
			) {
				id
				jql @required(action: THROW)
				fieldValue {
					__typename
					... on JiraJqlPriorityFieldValue {
						priority {
							priorityId
						}
					}
					... on JiraJqlUserFieldValue {
						user {
							accountId
						}
					}
					... on JiraJqlStatusFieldValue {
						status {
							id
						}
					}
					... on JiraJqlNumberFieldValue {
						number
					}
					... on JiraJqlOptionFieldValue {
						option {
							optionId
						}
					}
					... on JiraJqlSprintFieldValue {
						sprint {
							sprintId
						}
					}
					... on JiraJqlGoalsFieldValue {
						goal {
							id
						}
					}
				}
				...groupHeader_nativeIssueTable
				issues(fieldSetsInput: $fieldSetsInput, first: 50)
					# FirstGroup wouldn't be rendered if it has no issues, so issues is a required field at this point
					@required(action: THROW)
					@connection(key: "firstGroupSection_nativeIssueTable__issues") {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					edges {
						__typename
					}
					__id
					...hierarchy_nativeIssueTable_ChildRowsPagination_queryData
						@arguments(
							isInlineEditingEnabled: $isInlineEditingEnabled
							isReparentingEnabled: $isReparentingEnabled
							isDensityFull: $isDensityFull
							projectKeys: $projectKeys
							projectKey: $projectKey
							shouldQueryHasChildren: false
						)
				}
			}
		`,
		firstGroupRow,
	);

	const { columns } = useRowContext();
	const variables = useMemo(
		() => ({
			...getChildQueryVariablesForJql({
				cloudId,
				columns,
				jql: firstGroupRowData.jql,
				isReparentingEnabled: false,
			}),
			shouldQueryFieldSetsById: false,
			namespace: 'ISSUE_NAVIGATOR',
			shouldQueryHasChildren: false,
		}),
		[cloudId, columns, firstGroupRowData.jql],
	);

	const hasInitialised = useInitialiseFirstGroupIssues({
		groupId: firstGroupRowData.id,
		groupConnectionId,
		firstGroupItemsConnectionId: firstGroupRowData.issues.__id,
		issueSearchVariables: pick(variables, 'cloudId', 'issueSearchInput', 'fieldSetsInput'),
		loading,
	});

	const [isExpanded] = useIsItemExpanded({
		itemId: firstGroupRowData.id,
		groupId: firstGroupRowData.id,
		itemsConnectionId: groupConnectionId,
	});

	return (
		<>
			{isVirtual && (
				<GroupHeader
					groupHeader={firstGroupRowData}
					groupConnectionId={groupConnectionId}
					isFirstGroup
				/>
			)}
			{hasInitialised && isExpanded && (
				<FirstGroupIssues
					variables={variables}
					groupId={firstGroupRowData.id}
					groupConnectionId={groupConnectionId}
					groupFieldValue={getGroupFieldValue(firstGroupRowData.fieldValue)}
				/>
			)}
		</>
	);
};

type GroupRowProps = BaseProps & { groupRow: groupRow_nativeIssueTable_groupRow$key };

export const GroupRow = ({ groupRow, groupConnectionId, isVirtual }: GroupRowProps) => {
	const groupRowData = useFragment<groupRow_nativeIssueTable_groupRow$key>(
		graphql`
			fragment groupRow_nativeIssueTable_groupRow on JiraSpreadsheetGroup {
				id
				jql @required(action: THROW)
				fieldValue {
					__typename
					... on JiraJqlPriorityFieldValue {
						priority {
							priorityId
						}
					}
					... on JiraJqlUserFieldValue {
						user {
							accountId
						}
					}
					... on JiraJqlStatusFieldValue {
						status {
							id
						}
					}
					... on JiraJqlNumberFieldValue {
						number
					}
					... on JiraJqlOptionFieldValue {
						option {
							optionId
						}
					}
					... on JiraJqlSprintFieldValue {
						sprint {
							sprintId
						}
					}
					... on JiraJqlGoalsFieldValue {
						goal {
							id
						}
					}
				}
				...groupHeader_nativeIssueTable
			}
		`,
		groupRow,
	);

	const { renderChildRows } = useRowContext();

	return (
		groupRowData && (
			<>
				{isVirtual && (
					<GroupHeader groupHeader={groupRowData} groupConnectionId={groupConnectionId} />
				)}
				{renderChildRows?.({
					parentItemId: groupRowData.id,
					itemsConnectionId: groupConnectionId,
					depth: 0,
					type: 'GROUP_CHILDREN',
					groupFieldValue: getGroupFieldValue(groupRowData.fieldValue),
					groupJql: groupRowData.jql,
				})}
			</>
		)
	);
};
